import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { seo } from "@/lib/seo";
import { LegalPageSkeleton } from "@/components/skeletons";

export const Route = createFileRoute("/termos")({
  head: () =>
    seo({
      path: "/termos",
      title: "Termos de uso — Seu Bolso Tranquilo",
      description:
        "Termos de uso do Seu Bolso Tranquilo, app de controle financeiro pessoal. Saiba quais são os direitos e responsabilidades ao usar a plataforma.",
      breadcrumbs: [
        { name: "Início", path: "/" },
        { name: "Termos de uso", path: "/termos" },
      ],
    }),
  component: TermsPage,
  pendingComponent: LegalPageSkeleton,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo className="size-9" />
            <span className="font-display font-bold">Seu Bolso Tranquilo</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" /> Voltar
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary-glow mb-3">Legal</p>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-3">
          Termos de uso
        </h1>
        <p className="text-sm text-muted-foreground mb-10">Última atualização: 21 de abril de 2026</p>

        <div className="space-y-8 text-foreground/90 leading-relaxed">
          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">1. Identificação do prestador e aceitação</h2>
            <p className="text-muted-foreground">
              O serviço "Seu Bolso Tranquilo" ("Serviço") é fornecido por <strong>Thiago Henrique Ferreira Silva</strong>
              ("nós", "Prestador"). Ao criar uma conta e utilizar o Serviço, você ("Usuário") concorda integralmente com
              estes Termos de uso, formando um contrato vinculante diretamente com o Prestador. Se não concordar com algum
              dos termos, por favor, não utilize o Serviço.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">2. Descrição do serviço</h2>
            <p className="text-muted-foreground">
              O Seu Bolso Tranquilo é uma plataforma de organização financeira pessoal que permite registrar receitas, despesas
              e metas, oferecendo visualizações e insights baseados nas informações fornecidas pelo próprio usuário.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">3. Cadastro e conta</h2>
            <p className="text-muted-foreground">
              Para utilizar o Serviço você precisa criar uma conta com um e-mail válido. Você é responsável por manter
              suas credenciais em sigilo e por todas as atividades realizadas em sua conta.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">4. Uso aceitável</h2>
            <p className="text-muted-foreground">
              Você concorda em não utilizar o Serviço para fins ilegais, fraudulentos ou que possam prejudicar terceiros,
              o sistema ou a integridade dos dados de outros usuários.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">5. Planos e pagamentos</h2>
            <p className="text-muted-foreground">
              O Serviço oferece um plano gratuito com funcionalidades limitadas e planos pagos (Premium) com recursos
              adicionais. Nosso processo de pedidos é conduzido pelo nosso revendedor online <strong>Paddle.com</strong>.
              A Paddle.com é o Merchant of Record (revendedor oficial) de todos os nossos pedidos: cuida do processamento
              de pagamentos, faturamento, cobrança de impostos, atendimento sobre cobrança, cancelamentos e reembolsos.
              As condições de cobrança, renovação automática, impostos e estornos seguem os{" "}
              <a
                href="https://www.paddle.com/legal/checkout-buyer-terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-foreground hover:text-primary-glow"
              >
                Termos do Comprador da Paddle
              </a>{" "}
              aceitos no momento da compra. As condições específicas de reembolso estão descritas na nossa{" "}
              <a href="/reembolso" className="underline text-foreground hover:text-primary-glow">Política de reembolso</a>.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">6. Propriedade intelectual</h2>
            <p className="text-muted-foreground">
              O Serviço, incluindo todo o software, código, design, interfaces, marcas, logotipos, textos e demais
              elementos, é de propriedade exclusiva do Prestador e está protegido pelas leis de propriedade intelectual
              aplicáveis. Concedemos a você uma licença limitada, pessoal, não exclusiva e não transferível para utilizar
              o Serviço conforme estes Termos. Você não pode copiar, modificar, distribuir, vender, fazer engenharia
              reversa ou criar obras derivadas sem nossa autorização por escrito. Os dados financeiros que você insere
              continuam sendo seus.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">7. Limitação de responsabilidade</h2>
            <p className="text-muted-foreground">
              O Seu Bolso Tranquilo é uma ferramenta de apoio à organização financeira e não constitui aconselhamento
              financeiro, contábil ou de investimentos. As decisões tomadas com base nas informações são de responsabilidade
              exclusiva do usuário.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">8. Suspensão e encerramento</h2>
            <p className="text-muted-foreground">
              Você pode encerrar sua conta a qualquer momento. Reservamo-nos o direito de suspender ou encerrar o acesso
              de contas em caso de: violação material destes Termos, inadimplência, suspeita de fraude ou risco de
              segurança, ou violações repetidas das nossas políticas.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">9. Alterações</h2>
            <p className="text-muted-foreground">
              Estes Termos podem ser atualizados periodicamente. Notificaremos sobre mudanças relevantes por meio do próprio
              Serviço ou por e-mail.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-semibold mb-3">10. Contato</h2>
            <p className="text-muted-foreground">
              Em caso de dúvidas sobre estes Termos, entre em contato pelo canal disponibilizado dentro do aplicativo.
              Para questões diretamente relacionadas à cobrança e pagamentos, a Paddle também oferece atendimento em{" "}
              <a href="https://paddle.net" target="_blank" rel="noopener noreferrer" className="underline text-foreground hover:text-primary-glow">
                paddle.net
              </a>
              .
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-border/40 px-5 lg:px-8 py-8 mt-10">
        <div className="max-w-4xl mx-auto text-center text-sm text-muted-foreground">
          Seu Bolso Tranquilo © 2026
        </div>
      </footer>
    </div>
  );
}
